import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbAccordion, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { FormBuilderQuestionManageDrawerComponent } from '../form-builder-question-manage-drawer/form-builder-question-manage-drawer.component';
import { guidEmpty } from 'utils/helps';
import { ToastService } from '@core/components/toasts/toasts.service';
import { FormInputType } from 'services/models/form-builder/enum/form-input-type.enum';
import { FormItemOptionModel } from 'services/models/form-builder/form-item-option.model';
import { FormItemModel } from 'services/models/form-builder/form-item.model';
import { FormItemsOrganizerService } from '../services/form-items-organizer.service';
import {
  FormBuilderElementService,
  FormBuilderElementType,
} from '../services/form-builder-element.service';

@Component({
  selector: 'app-form-builder-group',
  templateUrl: './form-builder-group.component.html',
  styleUrls: ['./form-builder-group.component.scss'],
})
export class FormBuilderGroupComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() formGroup!: FormGroup;
  @Input() showChartType = false;
  @ViewChild('acc', { static: false }) accordion: NgbAccordion | undefined;

  public dragActive = false;
  public dragGroupActive = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private _modalService: NgbModal,
    private _toastService: ToastService,
    private _organizer: FormItemsOrganizerService,
    private _element: FormBuilderElementService
  ) { }

  ngOnInit(): void { }

  ngAfterViewInit(): void {
    this._tryOpenPanels();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this._tryOpenPanels();
  }

  public addGroup() {
    const groupsArray = this.formGroup.get('groups') as FormArray;
    groupsArray.push(this._createGroupFormGroup());
    this._tryOpenPanels();
  }

  private _tryOpenPanels() {
    if (this.accordion && this.groupsControls.length) {
      this.accordion.activeIds = this.groupsControls.map((_: any, i: number) => 'group-' + i);
    }
    this.cdr.detectChanges();
  }

  private _createGroupFormGroup(): FormGroup {
    return new FormGroup({
      label: new FormControl('', []),
      itens: new FormArray([]),
    });
  }

  private _createItemFormGroup(item: any): FormGroup {
    return new FormGroup({
      id: new FormControl(item?.id || guidEmpty, []),
      label: new FormControl(item?.label || '', []),
      inputType: new FormControl(item?.inputType || '', []),
      placeholder: new FormControl(item?.placeholder || '', []),
      description: new FormControl(item?.description || '', []),
      groupId: new FormControl(item?.groupId || guidEmpty, []),
      parentItemId: new FormControl(item?.parentItemId || null, []),
      parentItem: new FormControl(item?.parentItem || null),
      required: new FormControl(item?.required || false, []),
      order: new FormControl(item?.order || 0, []),
      chartType: new FormControl(item?.chartType || 'UNSET', []),
      itemChildren: new FormControl(item?.itemChildren || null),
      isChild: new FormControl(item?.isChild || false),
      options: new FormArray(
        (item?.options || []).map((opt: FormItemOptionModel) => this._createOptionGroup(opt))
      ),
    });
  }

  private _createOptionGroup(option?: FormItemOptionModel): FormGroup {
    return new FormGroup({
      id: new FormControl(option?.id || guidEmpty),
      formItemId: new FormControl(option?.id || guidEmpty),
      label: new FormControl(option?.label || '', Validators.required),
      legend: new FormControl(option?.legend || ''),
      order: new FormControl(option?.order || 0, Validators.required),
      isUpdate: new FormControl(option?.isUpdate || false),
      iconName: new FormControl(option?.iconName || null),
    });
  }

  public handleCreateOrUpdateQuestionItem(control?: any, itemControl?: any) {
    if (!control) return null;

    const itens = control.get('itens') as FormArray;
    if (!itens) return null;

    const parents = new FormArray(itens.controls.map(control => control));

    if (itemControl) {
      const index = parents.controls.indexOf(itemControl);
      if (index >= 0) {
        parents.removeAt(index);
      }
    }

    const modalRef = this._modalService.open(FormBuilderQuestionManageDrawerComponent, {
      animation: false,
    });

    modalRef.componentInstance.item = itemControl ? itemControl.value : null;
    modalRef.componentInstance.lastOrder = itemControl ? itemControl.value.order : itens.length + 1;
    modalRef.componentInstance.showChartType = this.showChartType;
    modalRef.componentInstance.hasAnotherNpsItem = this._hasAnotherNpsItem(
      itemControl?.value?.id || null
    );
    modalRef.componentInstance.parents = parents.value
      .filter(x => !x.isChild)
      .map((item: FormItemModel) => ({
        ...item,
        fullLabel: `${item.order} - ${item.label}`,
      })) as FormItemModel[];

    modalRef.closed.subscribe(response => {
      if (response?.item) {
        const itensArray = control.get('itens') as FormArray;
        if (itensArray) {
          const item = this._createItemFormGroup(response.item);
          this._organizer.insertOrReposition(itensArray, item, {
            oldControlRef: itemControl,
          });
          this.recreateItensControls(control);
        }
      }
    });
  }

  public onDropItem(event: CdkDragDrop<AbstractControl[]>, groupControl: AbstractControl) {
    const itensArray = groupControl.get('itens') as FormArray;
    if (!itensArray || !Array.isArray(itensArray.controls)) return;
    this._organizer.moveBlockOnDrop(itensArray, event.previousIndex, event.currentIndex, ctrl =>
      this.isChildItem(ctrl as any)
    );
  }

  public getItensControls(groupControl: AbstractControl): AbstractControl[] {
    const controls = (groupControl.get('itens') as FormArray)?.controls || [];
    return controls;
  }

  public recreateItensControls(groupControl: AbstractControl): AbstractControl[] {
    const itensArray = groupControl.get('itens') as FormArray;
    if (!itensArray) return [];

    const values = itensArray.value as FormItemModel[];

    // recria os controls do zero a partir dos valores
    const newControls = values.map(value => this._createItemFormGroup(value));

    // limpa o array original e adiciona os recriados
    itensArray.clear();
    newControls.forEach(ctrl => itensArray.push(ctrl));

    return itensArray.controls;
  }

  public updateItensOrder(groupControl: AbstractControl): void {
    const items = groupControl.get('itens') as FormArray;
    const controls = items?.controls || [];

    controls.forEach((control, index) => {
      control.get('order')?.setValue(index + 1);
    });

    items.updateValueAndValidity();
  }

  public moveGroupUp(index: number) {
    const groupsArray = this.formGroup.get('groups') as FormArray;
    this.moveControl(groupsArray.controls, index, index - 1);
  }

  public moveGroupDown(index: number) {
    const groupsArray = this.formGroup.get('groups') as FormArray;
    this.moveControl(groupsArray.controls, index, index + 1);
  }

  public getItemDescription(control: AbstractControl) {
    const value = control.value as FormItemModel;
    return FormInputType.getBy(value.inputType)?.label || '';
  }

  public getItemLabel(control: AbstractControl) {
    const value = control.value as FormItemModel;
    return `${value.order} - ${value.label}`;
  }

  public getGroupLabel(control: AbstractControl, index: number) {
    const value = control.value;
    return value.label || 'Grupo ' + (index + 1);
  }

  public isChildItem(control: AbstractControl) {
    const value = control.value as FormItemModel;
    return value.isChild;
  }

  private moveControl(controls: any[], from: number, to: number) {
    if (from === to || from < 0 || to < 0 || from >= controls.length || to >= controls.length)
      return;
    moveItemInArray(controls, from, to);
    controls.forEach((ctrl, idx) => ctrl.get('order')?.setValue(idx + 1));
  }

  private _hasAnotherNpsItem(currentItemId?: string | null): boolean {
    const groupsArray = this.formGroup.get('groups') as FormArray;
    const items = (groupsArray?.value || []).reduce(
      (acc: FormItemModel[], group: any) => acc.concat(this._flattenItems(group?.itens || [])),
      []
    );

    return items.some(
      (item: FormItemModel) =>
        item?.inputType === FormInputType.NPS.value && item?.id !== currentItemId
    );
  }

  private _flattenItems(items: FormItemModel[] = []): FormItemModel[] {
    return items.reduce(
      (acc: FormItemModel[], item: FormItemModel) =>
        acc.concat(item, this._flattenItems((item?.itemChildren as FormItemModel[]) || [])),
      []
    );
  }

  public removeGroup(index: number): void {
    const groups = this.groupsFormArray;
    const groupControl = groups.at(index);

    if (!groupControl) {
      this._toastService.showError('Grupo não encontrado.', 'top-center');
      return;
    }

    const payload = {
      groupId: groupControl.get('id')?.value,
      itemId: guidEmpty,
      optionId: guidEmpty,
    } as FormBuilderElementType;

    this._element.remove(
      payload,
      () => groups.removeAt(index),
      'Grupo removido com sucesso.',
      'Erro ao remover grupo.'
    );
  }

  public removeItem(groupControl: AbstractControl, itemControl: AbstractControl): void {
    const items = groupControl.get('itens') as FormArray;
    if (!items) {
      this._toastService.showError('Itens não encontrados no grupo.', 'top-center');
      return;
    }

    const index = items.controls.indexOf(itemControl);
    if (index === -1) {
      this._toastService.showError('Item não encontrado.', 'top-center');
      return;
    }

    const payload = {
      groupId: guidEmpty,
      itemId: itemControl.get('id')?.value,
      optionId: guidEmpty,
    } as FormBuilderElementType;

    this._element.remove(
      payload,
      () => items.removeAt(index),
      'Item removido com sucesso.',
      'Erro ao remover item.'
    );
  }

  public hasItemsInGroup(control: AbstractControl): boolean {
    const itens = control.get('itens') as FormArray;
    return Array.isArray(itens?.controls) && itens.controls.length > 0;
  }

  public get groupsControls(): AbstractControl[] {
    return this.formGroup ? (this.formGroup.get('groups') as FormArray)?.controls || [] : [];
  }

  public get groupsFormArray(): FormArray {
    return this.formGroup.get('groups') as FormArray;
  }

  public get isRemoving(): boolean {
    return this._element.isRemoving;
  }
}
